import { useMemo } from 'react';

import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';

import {
	type GenerateSearchUrlType,
	type NavigateCallback,
	type OnNavigate,
} from '../../../common/ui/app-context';
import { isOnSearchRoute, isSearchRouteUrl } from '../../../common/utils/url';
import { useWarmUpSearchPageQuery } from '../../../controllers/quick-find/warmup';
import { useQuickFindActions } from '../../../controllers/store/quick-find';

/**
 * Wraps the host-provided `onNavigate` so that a navigation toward the search
 * page first kicks off the search query warm-up to prime the backend
 * persisted-query cache.
 *
 * Behaviour notes:
 *
 * - When there is no `originalOnNavigate`, it is returned **by identity** so
 *   callers without a navigation handler are unaffected.
 * - Warm-up only fires when `isOnSearchRoute(generateSearchUrl)` returns `false`
 *   (i.e. we're definitively not already on the search page) AND the destination
 *   URL resolves to the search page route
 *   (`isSearchRouteUrl(url, generateSearchUrl) === true`). `true` (already on
 *   the search page) and `undefined` (route lookup failed) both skip warm-up,
 *   as do navigations whose destination is not the search page.
 */
export const useQuickFindOnNavigateWithWarmup = (
	originalOnNavigate: OnNavigate | undefined,
	generateSearchUrl: GenerateSearchUrlType | undefined,
): OnNavigate | undefined => {
	const warmUp = useWarmUpSearchPageQuery();
	const { cancelPendingPrefetch } = useQuickFindActions();

	return useMemo(() => {
		// Return original by identity when there is no navigation handler to wrap.
		if (!originalOnNavigate) {
			return originalOnNavigate;
		}

		const original = originalOnNavigate;
		const wrappedCallback: NavigateCallback = (url, action, event) => {
			if (UNSAFE_expValNoExposure('quick_find_prefetch', 'isEnabled', false)) {
				cancelPendingPrefetch();
			}
			// Only warm up when we definitively know we're NOT already on the
			// search page AND the destination is the search page itself. For
			// `isOnSearchRoute`, both `true` (already there) and `undefined`
			// (couldn't tell) skip warm-up.
			if (
				generateSearchUrl &&
				isOnSearchRoute(generateSearchUrl) === false &&
				isSearchRouteUrl(url, generateSearchUrl) === true
			) {
				// `event.timeStamp` anchors preload timings to the originating user
				// action (e.g. the Enter keypress) rather than to this callback, so
				// the measurement includes the work the key handler does before
				// navigating. It shares `performance.now()`'s time origin.
				warmUp(url, event?.timeStamp);
			}

			return typeof original === 'function'
				? original(url, action, event)
				: original.callback(url, action, event);
		};

		return typeof original === 'function'
			? wrappedCallback
			: { callback: wrappedCallback, options: original.options };
	}, [originalOnNavigate, generateSearchUrl, warmUp, cancelPendingPrefetch]);
};
